import { memo } from "react";

/** Week headers — Monday first (matches buildMonthCells). */
export const STATUS_CAL_WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const STATUS_CAL_LABEL = {
  present: "Present",
  halfday: "Half day",
  leave: "Leave",
  holiday: "Holiday",
  weekoff: "Week off",
  absent: "Absent",
};

/** Bottom bar colour — HRMS status tokens. */
export const STATUS_CAL_BAR = {
  present: "bg-[hsl(var(--hrms-present))]",
  halfday: "bg-[hsl(var(--hrms-half))]",
  leave: "bg-[hsl(var(--hrms-leave))]",
  holiday: "bg-[hsl(var(--hrms-half))]",
  weekoff: "bg-[hsl(var(--hrms-wo))]",
  absent: "bg-[hsl(var(--hrms-absent))]",
};

/**
 * One compact size for every calendar (admin attendance module baseline).
 * Landscape cells (~53px tall) — not aspect-square.
 */
const CELL = {
  gap: "gap-1",
  head: "text-[10px] py-0.5",
  cell: "min-h-[52px] rounded-lg p-1.5",
  day: "text-[10px]",
  label: "text-[8px] leading-tight",
  bar: "mt-auto h-1",
};

/**
 * One day tile: day number top-left, status label, coloured bottom bar.
 * size prop kept for API compat but all calendars share the same compact cell.
 */
export function StatusDayCell({
  day,
  kind = null,
  mute = false,
  isToday = false,
  selected = false,
  size: _size = "md",
  onClick,
  onDoubleClick,
  as: As = "div",
  title,
  className = "",
}) {
  const label = kind ? STATUS_CAL_LABEL[kind] : null;
  const bar = kind ? STATUS_CAL_BAR[kind] : null;

  return (
    <As
      type={As === "button" ? "button" : undefined}
      title={title || (mute ? undefined : label || "No status")}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      aria-current={isToday ? "date" : undefined}
      className={[
        "relative flex w-full flex-col overflow-hidden border text-left transition-colors",
        CELL.cell,
        mute
          ? "border-transparent bg-[hsl(var(--ds-muted)/0.45)]"
          : "border-[hsl(var(--hrms-line))] bg-[hsl(var(--ds-surface))]",
        selected
          ? "ring-2 ring-[hsl(var(--ds-primary))] ring-offset-1 ring-offset-[hsl(var(--ds-bg))]"
          : isToday
            ? "border-[hsl(var(--ds-primary))] ring-1 ring-[hsl(var(--ds-primary)/0.35)]"
            : "",
        As === "button" && !mute
          ? "cursor-pointer hover:bg-[hsl(var(--ds-muted))]"
          : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {!mute ? (
        <>
          <span
            className={`${CELL.day} font-semibold tabular-nums leading-none text-[hsl(var(--hrms-mute))]`}
          >
            {day}
          </span>
          {label ? (
            <span
              className={`${CELL.label} mt-0.5 font-medium text-[hsl(var(--hrms-ink))] line-clamp-2`}
            >
              {label}
            </span>
          ) : null}
          {bar ? (
            <span
              className={`block w-full shrink-0 rounded-full ${CELL.bar} ${bar}`}
              aria-hidden
            />
          ) : (
            <span className={`block ${CELL.bar}`} aria-hidden />
          )}
        </>
      ) : null}
    </As>
  );
}

/**
 * Month grid from buildMonthCells() output.
 * cells: { key, day, mute, kind, isToday }[]
 */
function StatusMonthCalendar({
  cells = [],
  size: _size = "md",
  weekdays = STATUS_CAL_WEEKDAYS,
  selectedDay = null,
  onDayClick,
  className = "",
  "aria-label": ariaLabel = "Month calendar",
}) {
  const interactive = typeof onDayClick === "function";

  return (
    <div className={className}>
      <div className={`mb-1 grid grid-cols-7 ${CELL.gap}`}>
        {weekdays.map((d) => (
          <span
            key={d}
            className={`${CELL.head} text-center font-semibold text-[hsl(var(--hrms-mute))]`}
          >
            {d}
          </span>
        ))}
      </div>
      <div className={`grid grid-cols-7 ${CELL.gap}`} aria-label={ariaLabel}>
        {cells.map((cell) => {
          const dayNum = cell.mute ? null : cell.day;
          return (
            <StatusDayCell
              key={cell.key}
              as={interactive && !cell.mute ? "button" : "div"}
              day={cell.day}
              kind={cell.kind}
              mute={cell.mute}
              isToday={!!cell.isToday}
              selected={
                selectedDay != null && dayNum != null && selectedDay === dayNum
              }
              onClick={
                interactive && !cell.mute
                  ? (e) => {
                      e.stopPropagation();
                      onDayClick(cell.day, cell);
                    }
                  : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
}

export default memo(StatusMonthCalendar);
